import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/http";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

// Live cost meter: placeholders expand (names, services, links), so estimate with typical lengths and count Twilio segments.
const SMS_FILL = { client_name: "Alexandra", client_first: "Alexandra", service: "Full Body Laser Hair Removal", business: "Your Clinic Name", date: "24/09/2026", time: "10:30", when: "24/09/2026 10:30", link: "https://azordi.app/b/xxxxxxxx", staff: "Jordan", amount: "$50.00", price: "$350.00", package: "6 Full Body Plan", expiry: "24/09/2027", sessions: "3", clinic: "Your Clinic Name" };
const SMS_EXT = new Set(["^", "{", "}", "\\", "[", "~", "]", "|", "€"]);
const GSM_RE = /^[@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&'()*+,\-./0-9:;<=>?¡A-ZÄÖÑÜ§¿a-zäöñüà^{}\\[~\]|€]*$/;
export const smsEstimate = (text) => {
  const filled = (text || "").replace(/\{(\w+)\}/g, (m, k) => SMS_FILL[k] || m);
  const gsm = GSM_RE.test(filled);
  const n = gsm ? [...filled].reduce((a, c) => a + (SMS_EXT.has(c) ? 2 : 1), 0) : filled.length;
  const segments = gsm ? (n <= 160 ? 1 : Math.ceil(n / 153)) : (n <= 70 ? 1 : Math.ceil(n / 67));
  const bad = [...new Set([...(text || "")].filter((c) => !GSM_RE.test(c)))];
  return { n, segments, gsm, bad };
};
const SmsMeter = ({ text, testId }) => {
  const e = smsEstimate(text);
  const tone = e.segments === 1 ? "text-muted-foreground" : e.segments === 2 ? "text-amber-700" : "text-destructive";
  return (
    <div className={`mt-1 text-[11px] flex flex-wrap gap-x-3 ${tone}`} data-testid={testId}>
      <span>≈ {e.n} chars with a typical name/service/link → <b>{e.segments} message{e.segments > 1 ? "s" : ""}</b> billed{e.segments > 1 ? " per send" : ""}</span>
      {!e.gsm && <span>Contains {e.bad.map((c) => `“${c}”`).join(" ")} — special characters are swapped for plain ones when sent (emoji removed) so you stay on 160-char messages.</span>}
      {e.gsm && e.segments > 1 && <span>Shorten to ≤160 chars to halve the cost.</span>}
    </div>
  );
};
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { MessageSquare, Save, Info, Clock, FileText, HeartPulse, Zap, Plus, Trash2, Pencil, Sparkles, Inbox, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { ServiceRule } from "@/components/booking/ServiceRule";

const FIELD_HINT = {
  client_name: "Client's name", service: "Service booked", clinic: "Your clinic name",
  staff: "Staff member", date: "Appointment date", time: "Appointment time", datetime: "Date & time",
  link: "One-tap booking link for the freed slot (added automatically if left out)",
};
const SAMPLE = {
  client_name: "Sarah", service: "Laser — Underarms", clinic: "your clinic", staff: "Alex",
  date: "Mon 8 Jun", time: "2:30 PM", datetime: "Mon 8 Jun, 2:30 PM", link: `${window.location.origin}/s/Qz8rTm`,
};

const AUTO_EMPTY = {
  name: "", target_type: "service", target_id: "", timing: "on_booking",
  frequency: "every", title: "Your treatment instructions", instructions: "",
  sms_text: "Hi {name}, here are your care instructions for {service}: {link}", active: true,
};
const TIMING = { on_booking: "When booked (before care)", reminder: "With reminder", after: "After checkout (aftercare)" };
const FREQ = { every: "Every time", once: "First time only" };

const STATUS_META = {
  sent: { label: "Sent", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  queued: { label: "Queued", cls: "bg-sky-100 text-sky-700 border-sky-200" },
  simulated: { label: "Simulated", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  failed: { label: "Failed", cls: "bg-red-100 text-red-700 border-red-200" },
  skipped_no_credit: { label: "No credit", cls: "bg-red-100 text-red-700 border-red-200" },
};
const KIND_LABEL = {
  welcome: "New client welcome",
  confirmation: "Booking confirmation", reminder: "Reminder", reschedule: "Reschedule", cancellation: "Cancellation",
  no_show: "No-show follow-up", rebook: "Rebook", review: "Review request", waitlist: "Waitlist", receipt: "Voucher receipt",
  aftercare: "Aftercare", consult: "Consult form", survey: "Survey", package_purchase: "Package purchased", package_update: "Package update",
};
const fmtWhen = (iso) => { try { const d = new Date(iso); return d.toLocaleString("en-AU", { day: "2-digit", month: "2-digit", year: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return iso || ""; } };

const smsInfo = (text, price) => {
  const len = (text || "").length;
  const seg = len === 0 ? 0 : len <= 160 ? 1 : Math.ceil(len / 153);
  const cost = (seg * (price || 0.15)).toFixed(2);
  return { len, seg, cost };
};
const render = (body, clinic) => {
  let out = body || "";
  const ctx = { ...SAMPLE, clinic: clinic || SAMPLE.clinic };
  Object.entries(ctx).forEach(([k, v]) => { out = out.replaceAll(`{${k}}`, v); });
  return out;
};

export default function Messages() {
  const [rules, setRules] = useState([]);
  const [mergeFields, setMergeFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [smsEnabled, setSmsEnabled] = useState(true);
  const [clinic, setClinic] = useState("");
  const [price, setPrice] = useState(0.15);
  const [consultTpls, setConsultTpls] = useState([]);
  const [acTpls, setAcTpls] = useState([]);
  const refs = useRef({});

  // Custom automations
  const [autos, setAutos] = useState([]);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [autoForm, setAutoForm] = useState(AUTO_EMPTY);
  const [autoEditId, setAutoEditId] = useState(null);
  const [autoOpen, setAutoOpen] = useState(false);
  const [drafting, setDrafting] = useState(false);

  // Sent message log
  const [log, setLog] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [logSearch, setLogSearch] = useState("");
  const [logKind, setLogKind] = useState("all");
  const [logStatus, setLogStatus] = useState("all");
  const [resending, setResending] = useState("");
  const loadLog = () => {
    setLogLoading(true);
    api.get("/sms/messages").then(({ data }) => setLog(data || [])).catch(() => {}).finally(() => setLogLoading(false));
  };
  const resend = async (m) => {
    setResending(m.id);
    try {
      const { data } = await api.post(`/sms/messages/${m.id}/resend`);
      const st = data?.status;
      if (st === "sent" || st === "queued") toast.success("Message resent");
      else if (st === "simulated") toast.info("Resent (simulated — live delivery pending Twilio approval)");
      else toast.warning(`Resend status: ${st || "unknown"}`);
      loadLog();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't resend"); }
    finally { setResending(""); }
  };
  const logKinds = Array.from(new Set(log.map((m) => m.kind).filter(Boolean)));
  const filteredLog = log.filter((m) => {
    if (logKind !== "all" && m.kind !== logKind) return false;
    if (logStatus !== "all" && m.status !== logStatus) return false;
    const q = logSearch.trim().toLowerCase();
    if (q && !((m.client_name || "").toLowerCase().includes(q) || (m.to || "").toLowerCase().includes(q) || (m.body || "").toLowerCase().includes(q))) return false;
    return true;
  });

  const load = () => {
    api.get("/messages/rules").then(({ data }) => { setRules(data.rules || []); setMergeFields(data.merge_fields || []); }).catch(() => {});
    api.get("/business").then(({ data }) => { setSmsEnabled(!!data.sms_enabled); setClinic(data.name || ""); setPrice(data.sms_price_per_msg || 0.15); }).catch(() => {});
    api.get("/forms/templates").then(({ data }) => setConsultTpls(data)).catch(() => {});
    api.get("/aftercare/templates").then(({ data }) => setAcTpls(data)).catch(() => {});
    api.get("/automations").then(({ data }) => setAutos(data)).catch(() => {});
    api.get("/services").then(({ data }) => setServices(data)).catch(() => {});
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
  };
  useEffect(() => { load(); loadLog(); }, []);

  const setRule = (kind, patch) => setRules((rs) => rs.map((r) => (r.kind === kind ? { ...r, ...patch } : r)));
  const setCfg = (kind, patch) => setRules((rs) => rs.map((r) => (r.kind === kind ? { ...r, config: { ...(r.config || {}), ...patch } } : r)));

  const insertField = (kind, field) => {
    const el = refs.current[kind];
    const token = `{${field}}`;
    const cur = rules.find((r) => r.kind === kind)?.body || "";
    if (el && typeof el.selectionStart === "number") {
      const s = el.selectionStart, e = el.selectionEnd;
      const next = cur.slice(0, s) + token + cur.slice(e);
      setRule(kind, { body: next });
      requestAnimationFrame(() => { try { el.focus(); el.setSelectionRange(s + token.length, s + token.length); } catch (_) {} });
    } else { setRule(kind, { body: cur + token }); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/messages/rules", { rules: rules.map((r) => ({ kind: r.kind, enabled: r.enabled, body: r.body, config: r.config || null })) });
      setRules(data.rules || []);
      toast.success("Messages saved");
    } catch (e) { toast.error("Couldn't save — please try again"); }
    finally { setSaving(false); }
  };

  // ---- automations ----
  const autoSet = (k, v) => setAutoForm((f) => ({ ...f, [k]: v }));
  const autoTargetName = (a) => (a.target_type === "service" ? services.find((s) => s.id === a.target_id)?.name : categories.find((c) => c.id === a.target_id)?.name) || "—";
  const autoTargets = autoForm.target_type === "service" ? services : categories;
  const openNewAuto = () => { setAutoForm(AUTO_EMPTY); setAutoEditId(null); setAutoOpen(true); };
  const openEditAuto = (a) => { setAutoForm({ ...AUTO_EMPTY, ...a }); setAutoEditId(a.id); setAutoOpen(true); };
  const draftAuto = async () => {
    if (!autoForm.target_id) { toast.error("Pick a treatment or category first"); return; }
    setDrafting(true);
    try {
      const kind = autoForm.timing === "after" ? "after" : autoForm.timing === "reminder" ? "both" : "before";
      const { data } = await api.post("/automations/draft", { target_name: autoTargetName(autoForm), kind });
      autoSet("instructions", data.instructions);
      toast.success("Instructions drafted — edit as you like");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't draft"); }
    finally { setDrafting(false); }
  };
  const saveAuto = async () => {
    if (!autoForm.name.trim() || !autoForm.target_id) { toast.error("Add a name and pick a target"); return; }
    if (!autoForm.instructions.trim()) { toast.error("Add or draft the instructions"); return; }
    try {
      if (autoEditId) await api.put(`/automations/${autoEditId}`, autoForm);
      else await api.post("/automations", autoForm);
      setAutoOpen(false); load(); toast.success("Automation saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  const removeAuto = async (a) => { if (!window.confirm(`Delete "${a.name}"?`)) return; try { await api.delete(`/automations/${a.id}`); load(); toast.success("Deleted"); } catch { toast.error("Failed"); } };
  const toggleAuto = async (a) => { try { await api.put(`/automations/${a.id}`, { ...a, active: !a.active }); load(); } catch { toast.error("Failed"); } };

  const reminderCfg = (r) => r.config || {};

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-8" data-testid="messages-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight flex items-center gap-2">
            <MessageSquare className="h-7 w-7 text-primary" />Customer Messages
          </h1>
          <p className="text-muted-foreground mt-1">Every automated text your clients get, in one place. Edit the wording, set timing, and toggle each on or off.</p>
        </div>
        <Button className="rounded-lg h-11" onClick={save} disabled={saving} data-testid="save-messages-button">
          <Save className="h-4 w-4 mr-2" />{saving ? "Saving…" : "Save changes"}
        </Button>
      </div>

      {!smsEnabled && (
        <div className="mt-4 flex items-start gap-2 rounded-xl bg-amber-50 text-amber-800 text-sm p-3 border border-amber-200" data-testid="sms-disabled-notice">
          <Info className="h-4 w-4 mt-0.5 shrink-0" />
          <span>SMS is currently turned off in <b>Business Info</b>. Turn it on there so these messages actually send. Until then, sends are logged/simulated.</span>
        </div>
      )}

      <Tabs defaultValue="templates" className="mt-6" onValueChange={(v) => { if (v === "log") loadLog(); }}>
        <TabsList data-testid="messages-tabs">
          <TabsTrigger value="templates" data-testid="tab-templates">Templates & automations</TabsTrigger>
          <TabsTrigger value="log" data-testid="tab-log">Sent log</TabsTrigger>
        </TabsList>

        <TabsContent value="templates">
      <div className="text-xs font-mono-label uppercase tracking-wider text-muted-foreground mt-8 mb-3">Standard messages</div>
      <div className="space-y-4">
        {rules.map((r) => {
          let previewText = render(r.body, clinic);
          if (r.kind === "waitlist" && !(r.body || "").includes("{link}")) previewText += ` Tap to take it: ${window.location.origin}/s/Qz8rTm`;
          if (r.kind === "reminder" || r.kind === "confirmation" || r.kind === "welcome") {
            const cfg = r.config || {};
            if (cfg.attach_consult_form_id) { const t = consultTpls.find((x) => x.id === cfg.attach_consult_form_id); previewText += `\n\nComplete your ${t?.name || "form"}: ${window.location.origin}/s/Ab3xYz`; }
            if (cfg.attach_aftercare_template_id) { previewText += `\n\nYour instructions: ${window.location.origin}/s/Kp7mQd`; }
          }
          const info = smsInfo(previewText, price);
          return (
            <Card key={r.kind} className="rounded-2xl border p-5 shadow-sm" data-testid={`rule-${r.kind}`}>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-heading font-bold text-lg">{r.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{r.trigger}</div>
                </div>
                <Switch checked={!!r.enabled} onCheckedChange={(v) => setRule(r.kind, { enabled: v })} data-testid={`rule-${r.kind}-toggle`} />
              </div>

              {r.kind === "reminder" && (
                <div className={`mt-4 rounded-xl border bg-muted/20 p-4 space-y-4 ${r.enabled ? "" : "opacity-60"}`} data-testid="reminder-timing">
                  <div className="flex items-center gap-2 text-sm font-medium"><Clock className="h-4 w-4 text-primary" />When should it send?</div>
                  <div className="flex flex-wrap items-center gap-3">
                    <Select value={reminderCfg(r).timing_mode || "hours_before"} onValueChange={(v) => setCfg(r.kind, { timing_mode: v })}>
                      <SelectTrigger className="h-9 rounded-lg w-44" data-testid="reminder-mode"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hours_before">Hours before</SelectItem>
                        <SelectItem value="days_prior">Days prior, at a time</SelectItem>
                      </SelectContent>
                    </Select>
                    {(reminderCfg(r).timing_mode || "hours_before") === "hours_before" ? (
                      <div className="flex items-center gap-2 text-sm">
                        <Input type="number" min={1} className="h-9 w-20 rounded-lg" value={reminderCfg(r).hours_before ?? 24} onChange={(e) => setCfg(r.kind, { hours_before: parseInt(e.target.value || "0", 10) })} data-testid="reminder-hours" />
                        <span className="text-muted-foreground">hours before the appointment</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-sm">
                        <Input type="number" min={0} className="h-9 w-20 rounded-lg" value={reminderCfg(r).days_prior ?? 1} onChange={(e) => setCfg(r.kind, { days_prior: parseInt(e.target.value || "0", 10) })} data-testid="reminder-days" />
                        <span className="text-muted-foreground">day(s) prior at</span>
                        <Input type="time" className="h-9 w-28 rounded-lg" value={reminderCfg(r).send_time || "10:00"} onChange={(e) => setCfg(r.kind, { send_time: e.target.value })} data-testid="reminder-time" />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {r.kind === "rebook" && (
                <div className={`mt-4 rounded-xl border bg-muted/20 p-4 space-y-4 ${r.enabled ? "" : "opacity-60"}`} data-testid="rebook-rules">
                  <div className="flex items-center gap-2 text-sm font-medium"><Clock className="h-4 w-4 text-primary" />When should it send?</div>
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    <span className="text-muted-foreground">After the visit is checked out, send</span>
                    <Input type="number" min={0} className="h-9 w-20 rounded-lg" value={reminderCfg(r).delay_value ?? 2} onChange={(e) => setCfg(r.kind, { delay_value: parseInt(e.target.value || "0", 10) })} data-testid="rebook-delay-value" />
                    <Select value={reminderCfg(r).delay_unit || "hours"} onValueChange={(v) => setCfg(r.kind, { delay_unit: v })}>
                      <SelectTrigger className="h-9 w-32 rounded-lg" data-testid="rebook-delay-unit"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="hours">hours</SelectItem><SelectItem value="days">days</SelectItem><SelectItem value="weeks">weeks</SelectItem></SelectContent>
                    </Select>
                    <span className="text-muted-foreground">later.</span>
                  </div>
                  <ServiceRule label="For a visit containing:" value={reminderCfg(r).for_services || []} onChange={(v) => setCfg(r.kind, { for_services: v })} testid="rebook-for" />
                  <ServiceRule label="And the client has no future appointment containing:" value={reminderCfg(r).unless_future_services || []} onChange={(v) => setCfg(r.kind, { unless_future_services: v })} testid="rebook-unless" />
                  <p className="text-[11px] text-muted-foreground">Rules are checked at send time, so if the client rebooks at the desk the text is skipped. Looking for the longer "weeks since last visit" nudge? That lives in CRM → Re-booking.</p>
                </div>
              )}

              {r.kind === "review" && (
                <div className={`mt-4 rounded-xl border bg-muted/20 p-4 space-y-4 ${r.enabled ? "" : "opacity-60"}`} data-testid="review-timing">
                  <div className="flex items-center gap-2 text-sm font-medium"><Clock className="h-4 w-4 text-primary" />When should it send?</div>
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    <span className="text-muted-foreground">After a client's</span>
                    <Input type="number" min={1} className="h-9 w-20 rounded-lg" value={reminderCfg(r).after_visit ?? 3} onChange={(e) => setCfg(r.kind, { after_visit: parseInt(e.target.value || "1", 10) })} data-testid="review-after-visit" />
                    <span className="text-muted-foreground">completed visit, sent</span>
                    <Select value={String(reminderCfg(r).delay_hours ?? 24)} onValueChange={(v) => setCfg(r.kind, { delay_hours: parseInt(v, 10) })}>
                      <SelectTrigger className="h-9 rounded-lg w-28" data-testid="review-delay"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="12">12 hours</SelectItem>
                        <SelectItem value="24">24 hours</SelectItem>
                        <SelectItem value="48">48 hours</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="text-muted-foreground">later — once only per client.</span>
                  </div>
                </div>
              )}

                <div className={`mt-4 rounded-xl border bg-muted/20 p-4 space-y-3 ${r.enabled ? "" : "opacity-60"}`} data-testid={`${r.kind}-attachments`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-sm"><FileText className="h-4 w-4 text-primary" />Attach a form</div>
                    <Switch checked={!!reminderCfg(r).attach_consult_form_id} onCheckedChange={(v) => setCfg(r.kind, { attach_consult_form_id: v ? (consultTpls[0]?.id || "") : "" })} data-testid={`${r.kind}-attach-consult-toggle`} />
                  </div>
                  {reminderCfg(r).attach_consult_form_id && (
                    <Select value={reminderCfg(r).attach_consult_form_id} onValueChange={(v) => setCfg(r.kind, { attach_consult_form_id: v })}>
                      <SelectTrigger className="h-9 rounded-lg" data-testid={`${r.kind}-consult-select`}><SelectValue placeholder="Choose a form" /></SelectTrigger>
                      <SelectContent>{consultTpls.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-sm"><HeartPulse className="h-4 w-4 text-primary" />Attach {r.kind === "confirmation" ? "prep / instructions" : "aftercare / instructions"}</div>
                    <Switch checked={!!reminderCfg(r).attach_aftercare_template_id} onCheckedChange={(v) => setCfg(r.kind, { attach_aftercare_template_id: v ? (acTpls[0]?.id || "") : "" })} data-testid={`${r.kind}-attach-aftercare-toggle`} />
                  </div>
                  {reminderCfg(r).attach_aftercare_template_id && (
                    <Select value={reminderCfg(r).attach_aftercare_template_id} onValueChange={(v) => setCfg(r.kind, { attach_aftercare_template_id: v })}>
                      <SelectTrigger className="h-9 rounded-lg" data-testid={`${r.kind}-aftercare-select`}><SelectValue placeholder="Choose instructions" /></SelectTrigger>
                      <SelectContent>{acTpls.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                  <p className="text-[11px] text-muted-foreground">A secure link is added to the text so the client can complete the form or read their instructions.</p>
                </div>

              <Textarea
                ref={(el) => (refs.current[r.kind] = el)}
                value={r.body}
                onChange={(e) => setRule(r.kind, { body: e.target.value })}
                rows={3}
                className={`mt-3 rounded-lg ${r.enabled ? "" : "opacity-60"}`}
                data-testid={`rule-${r.kind}-body`}
              />
              <SmsMeter text={r.body} testId={`rule-${r.kind}-meter`} />
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(r.kind === "welcome" ? mergeFields.filter((f) => f === "client_name" || f === "clinic") : r.kind === "waitlist" ? [...mergeFields, "link"] : mergeFields).map((f) => (
                  <button key={f} type="button" onClick={() => insertField(r.kind, f)} title={FIELD_HINT[f] || f}
                    className="text-xs rounded-full border px-2.5 py-1 bg-muted/40 hover:bg-primary/10 hover:border-primary/30 transition-colors"
                    data-testid={`rule-${r.kind}-field-${f}`}>{`{${f}}`}</button>
                ))}
              </div>

              <div className="mt-3 rounded-xl bg-primary/5 border border-primary/10 p-3" data-testid={`rule-${r.kind}-preview`}>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Preview</div>
                <p className="text-sm whitespace-pre-line text-foreground/90">{previewText || "—"}</p>
                <div className="text-[11px] text-muted-foreground mt-2 tabular-nums">{info.len} chars · ≈ {info.seg} SMS · ≈ ${info.cost}</div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Custom automations */}
      <div className="flex items-center justify-between mt-10 mb-3">
        <div className="text-xs font-mono-label uppercase tracking-wider text-muted-foreground">Custom automations (AI care instructions)</div>
        <Button size="sm" variant="outline" className="rounded-lg" onClick={openNewAuto} data-testid="automation-new"><Plus className="h-4 w-4 mr-1" />New</Button>
      </div>
      {autos.length === 0 ? (
        <Card className="rounded-2xl border border-dashed p-8 text-center text-muted-foreground" data-testid="automations-empty">
          <Zap className="h-7 w-7 mx-auto mb-2 text-primary/60" />
          No custom automations yet. Attach AI-written before/after care to a treatment or category.
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {autos.map((a) => (
            <Card key={a.id} className="rounded-2xl border p-4 shadow-sm" data-testid={`automation-${a.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-heading font-semibold truncate">{a.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5 truncate">{a.target_type === "service" ? "Treatment" : "Category"}: {autoTargetName(a)}</div>
                </div>
                <Switch checked={a.active} onCheckedChange={() => toggleAuto(a)} data-testid={`automation-active-${a.id}`} />
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                <Badge variant="secondary" className="text-[10px]">{TIMING[a.timing]}</Badge>
                <Badge variant="secondary" className="text-[10px]">{FREQ[a.frequency]}</Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-3 line-clamp-2">{a.instructions}</p>
              <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                <Button variant="ghost" size="sm" className="h-8" onClick={() => openEditAuto(a)} data-testid={`automation-edit-${a.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>
                <Button variant="ghost" size="sm" className="h-8 text-red-500" onClick={() => removeAuto(a)} data-testid={`automation-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5 mr-1" />Delete</Button>
              </div>
            </Card>
          ))}
        </div>
      )}
        </TabsContent>

        <TabsContent value="log">
          <div className="flex items-center justify-between mt-6 mb-3">
            <div>
              <div className="text-xs font-mono-label uppercase tracking-wider text-muted-foreground">Everything sent</div>
              <p className="text-sm text-muted-foreground mt-0.5">Every text sent from your clinic — confirmations, reminders, aftercare and more.</p>
            </div>
            <Button variant="outline" size="sm" className="rounded-lg" onClick={loadLog} disabled={logLoading} data-testid="log-refresh">
              <RefreshCw className={`h-4 w-4 mr-1.5 ${logLoading ? "animate-spin" : ""}`} />Refresh
            </Button>
          </div>
          {log.length === 0 ? (
            <Card className="rounded-2xl border border-dashed p-10 text-center text-muted-foreground" data-testid="log-empty">
              <Inbox className="h-7 w-7 mx-auto mb-2 text-primary/60" />
              {logLoading ? "Loading…" : "No messages sent yet. As soon as a text goes out, it'll appear here."}
            </Card>
          ) : (
            <>
            <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="log-filters">
              <Input placeholder="Search name, phone or text…" value={logSearch} onChange={(e) => setLogSearch(e.target.value)} className="h-9 rounded-lg w-full sm:w-64" data-testid="log-search" />
              <Select value={logKind} onValueChange={setLogKind}>
                <SelectTrigger className="h-9 rounded-lg w-44" data-testid="log-filter-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {logKinds.map((k) => <SelectItem key={k} value={k}>{KIND_LABEL[k] || k}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={logStatus} onValueChange={setLogStatus}>
                <SelectTrigger className="h-9 rounded-lg w-40" data-testid="log-filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="queued">Queued</SelectItem>
                  <SelectItem value="simulated">Simulated</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="skipped_no_credit">No credit</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground tabular-nums ml-auto" data-testid="log-count">{filteredLog.length} of {log.length}</span>
            </div>
            {filteredLog.length === 0 ? (
              <Card className="rounded-2xl border border-dashed p-8 text-center text-muted-foreground" data-testid="log-no-match">No messages match these filters.</Card>
            ) : (
            <div className="space-y-2" data-testid="log-list">
              {filteredLog.map((m) => {
                const st = STATUS_META[m.status] || { label: m.status || "—", cls: "bg-muted text-muted-foreground border-border" };
                return (
                  <Card key={m.id} className="rounded-xl border p-4 shadow-sm" data-testid={`log-item-${m.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-heading font-semibold text-sm">{m.client_name || m.to || "Unknown"}</span>
                          {m.client_name && <span className="text-xs text-muted-foreground tabular-nums">{m.to}</span>}
                          <Badge variant="secondary" className="text-[10px]">{KIND_LABEL[m.kind] || m.kind || "Message"}</Badge>
                        </div>
                        <p className="text-sm text-foreground/90 mt-1.5 whitespace-pre-line break-words">{m.body}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <span className={`inline-block text-[10px] font-medium rounded-full border px-2 py-0.5 ${st.cls}`} data-testid={`log-status-${m.id}`}>{st.label}</span>
                        <div className="text-[11px] text-muted-foreground mt-1.5 tabular-nums">{fmtWhen(m.created_at)}</div>
                        <div className="text-[11px] text-muted-foreground tabular-nums">{m.segments || 1} SMS · ${Number(m.cost || 0).toFixed(2)}</div>
                      </div>
                    </div>
                    {(m.status === "failed" || m.status === "skipped_no_credit") && (
                      <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
                        {m.error?.message ? <div className="text-[11px] text-red-600 bg-red-50 border border-red-100 rounded-lg px-2 py-1.5 flex-1 min-w-0 break-words">{m.error.message}</div> : <span />}
                        <Button variant="outline" size="sm" className="h-8 rounded-lg shrink-0" onClick={() => resend(m)} disabled={resending === m.id} data-testid={`log-resend-${m.id}`}>
                          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${resending === m.id ? "animate-spin" : ""}`} />{resending === m.id ? "Resending…" : "Resend"}
                        </Button>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
            )}
            </>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={autoOpen} onOpenChange={setAutoOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col gap-0">
          <DialogHeader className="shrink-0 pb-2"><DialogTitle className="font-heading">{autoEditId ? "Edit automation" : "New automation"}</DialogTitle><DialogDescription>Attach an AI-written care message to a treatment or category — it sends automatically as a text with a secure link.</DialogDescription></DialogHeader>
          <div className="flex-1 overflow-y-auto -mx-6 px-6 py-3 space-y-4">
            <div><Label className="text-xs">Name</Label><Input value={autoForm.name} onChange={(e) => autoSet("name", e.target.value)} placeholder="First-time laser before & after care" className="h-10 rounded-lg mt-1" data-testid="automation-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Attach to</Label>
                <Select value={autoForm.target_type} onValueChange={(v) => { autoSet("target_type", v); autoSet("target_id", ""); }}>
                  <SelectTrigger className="h-10 rounded-lg mt-1" data-testid="automation-target-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="service">A treatment</SelectItem><SelectItem value="category">A category</SelectItem></SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{autoForm.target_type === "service" ? "Treatment" : "Category"}</Label>
                <Select value={autoForm.target_id} onValueChange={(v) => autoSet("target_id", v)}>
                  <SelectTrigger className="h-10 rounded-lg mt-1" data-testid="automation-target"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>{autoTargets.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">When to send</Label>
                <Select value={autoForm.timing} onValueChange={(v) => autoSet("timing", v)}>
                  <SelectTrigger className="h-10 rounded-lg mt-1" data-testid="automation-timing"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(TIMING).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">How often</Label>
                <Select value={autoForm.frequency} onValueChange={(v) => autoSet("frequency", v)}>
                  <SelectTrigger className="h-10 rounded-lg mt-1" data-testid="automation-frequency"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(FREQ).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs">Instructions (shown on the secure link page)</Label>
                <Button variant="outline" size="sm" className="h-8 rounded-lg" onClick={draftAuto} disabled={drafting} data-testid="automation-draft">
                  <Sparkles className="h-3.5 w-3.5 mr-1" />{drafting ? "Drafting…" : "Draft with AI"}
                </Button>
              </div>
              <Textarea value={autoForm.instructions} onChange={(e) => autoSet("instructions", e.target.value)} rows={8} placeholder="Draft with AI, then edit the wording…" className="rounded-lg" data-testid="automation-instructions" />
            </div>
            <div><Label className="text-xs">Page title</Label><Input value={autoForm.title} onChange={(e) => autoSet("title", e.target.value)} className="h-10 rounded-lg mt-1" data-testid="automation-title" /></div>
            <div>
              <Label className="text-xs">Text message</Label>
              <Textarea value={autoForm.sms_text} onChange={(e) => autoSet("sms_text", e.target.value)} rows={2} className="rounded-lg mt-1" data-testid="automation-sms-text" />
              <SmsMeter text={autoForm.sms_text} testId="automation-sms-meter" />
              <p className="text-[11px] text-muted-foreground mt-1">Use {"{name}"}, {"{service}"}, {"{link}"}, {"{business}"}. The link opens the instructions page.</p>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-sm">Active</Label>
              <Switch checked={autoForm.active} onCheckedChange={(v) => autoSet("active", v)} data-testid="automation-active-toggle" />
            </div>
          </div>
          <DialogFooter className="shrink-0 border-t pt-4"><Button onClick={saveAuto} className="rounded-lg" data-testid="automation-save">{autoEditId ? "Save changes" : "Create automation"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
